import React, { useState } from 'react';
import { getCategoryIcon, getCategoryGradient } from '../utils/db';
import './ProductCardGrid.css';

// ── DEV: local icon overrides for testing before Firestore iconUrl is populated ──
function getIconOverride(product) {
  const name = (product.name || '').toLowerCase();
  if (name.includes('milk'))                                return '/product-icons/milk.png';
  if (name.includes('grated') && name.includes('cheese'))   return '/product-icons/grated-cheese.png';
  if (name.includes('sliced') && name.includes('cheese'))   return '/product-icons/sliced-cheese.png';
  if (name.includes('cheese'))                              return '/product-icons/block-cheese.png';
  if (name.includes('olive oil'))                           return '/product-icons/olive-oil.png';
  if (name.includes('spaghetti'))                           return '/product-icons/spaghetti.png';
  if (name.includes('egg'))                                 return '/product-icons/eggs.png';
  if (name.includes('baked bean'))                          return '/product-icons/baked-beans.png';
  if (name.includes('unsalted') && name.includes('butter')) return '/product-icons/unsalted-butter.png';
  if (name.includes('butter'))                              return '/product-icons/salted-butter.png';
  if (name.includes('brown') && name.includes('bread'))     return '/product-icons/brown-bread.png';
  if (name.includes('seeded') && name.includes('bread'))    return '/product-icons/seeded-bread.png';
  if (name.includes('white') && name.includes('bread'))     return '/product-icons/white-bread.png';
  if (name.includes('banana'))                              return '/product-icons/bananas.png';
  return null;
}

function isTallIcon(product) {
  const name = (product?.name || '').toLowerCase();
  return name.includes('milk') || name.includes('olive oil') || name.includes('spaghetti');
}

function formatStoreId(storeId) {
  return (storeId || '')
    .replace(/^store_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

// ── Mixed-family helpers ──────────────────────────────────────────────────────

// A family is "mixed" when it has at least one own-brand sibling AND at least
// one branded sibling. Only then do we switch to the grouped card layout.
function isMixedFamily(siblings) {
  return (
    siblings.some(s => s.ownBrand === true) &&
    siblings.some(s => s.ownBrand === false && s.tier === 'brand')
  );
}

function cheapestPriceEntry(prices) {
  const valid = prices.filter(p => p.price > 0);
  if (!valid.length) return null;
  return valid.reduce((best, p) => (p.price < best.price ? p : best), valid[0]);
}

// Card view: one entry per store, cheapest absolute price.
// Per-unit value logic (price per 100ml/100g) belongs in the full compare view.
function cheapestAbsDedupeByStore(prices) {
  const byStore = {};
  prices.forEach(p => {
    if (!byStore[p.storeId] || p.price < byStore[p.storeId].price) {
      byStore[p.storeId] = p;
    }
  });
  return Object.values(byStore).sort((a, b) => a.price - b.price);
}

function ProductCardGrid({ products, allProducts = [], stores, onAddToBasket, onSelectProduct }) {
  const [expandedCardId, setExpandedCardId] = useState(null);

  const getStoreName = (storeId) => {
    const normId = (storeId || '').replace(/^store_/, '');
    const store  = stores.find(s => s.id === normId || s.id === storeId);
    if (store) return store.name;
    return formatStoreId(storeId);
  };

  const getSortedStorePrices = (product) => {
    if (!product.prices) return [];
    return product.prices
      .filter(p => p.price > 0)
      .map(p => ({ ...p, storeName: getStoreName(p.storeId) }))
      .sort((a, b) => a.price - b.price);
  };

  if (!products || products.length === 0) return null;

  return (
    <div className="pcg-grid">
      {products.map(product => {
        const isExpanded = expandedCardId === product.id;
        const iconUrl    = product.iconUrl || getIconOverride(product);

        // Compute siblings from the full undeduped product list
        const siblings = allProducts.filter(p => p.productId === (product.productId || product.id));
        const mixed    = siblings.length > 1 && isMixedFamily(siblings);


        // ── Mixed-family card ─────────────────────────────────────────────
        if (mixed) {
          const ownBrandSiblings = siblings.filter(s => s.ownBrand === true);
          const brandedSiblings  = siblings.filter(s => s.ownBrand === false && s.tier === 'brand');

          const allOwnPrices     = ownBrandSiblings.flatMap(s => (s.prices || []).filter(p => p.price > 0));
          const allBrandedPrices = brandedSiblings.flatMap(s => (s.prices || []).filter(p => p.price > 0));

          const cheapestOwn     = cheapestPriceEntry(allOwnPrices);
          const cheapestBranded = cheapestPriceEntry(allBrandedPrices);

          // Short Penny line: "Heinz is £1.13 more"
          let pennyLine = null;
          if (cheapestOwn && cheapestBranded) {
            const diff = cheapestBranded.price - cheapestOwn.price;
            if (diff > 0) {
              const brandName = cheapestBranded.brand || 'Branded';
              const diffStr   = diff >= 1
                ? `£${diff.toFixed(2)}`
                : `${Math.round(diff * 100)}p`;
              pennyLine = `${brandName} is ${diffStr} more`;
            }
          }

          // Expanded rows: one per store across all tiers, best value per unit
          const allMixedPrices = [...allOwnPrices, ...allBrandedPrices];
          const expandedRows   = cheapestAbsDedupeByStore(allMixedPrices);

          return (
            <div
              key={product.id}
              className={`pcg-card pcg-card--mixed${isExpanded ? ' expanded' : ''}`}
              onClick={() => setExpandedCardId(isExpanded ? null : product.id)}
            >
              {/* Icon */}
              <div
                className="pcg-card-image"
                style={iconUrl ? undefined : { background: getCategoryGradient(product.category) }}
                aria-hidden="true"
              >
                {iconUrl
                  ? <img src={iconUrl} alt="" className={`pcg-card-photo${isTallIcon(product) ? ' pcg-card-photo--tall' : ''}`} />
                  : <span className="pcg-card-icon">{getCategoryIcon(product.category)}</span>
                }
              </div>

              {/* ── Collapsed: summary rows ── */}
              <div className="pcg-collapsed">
                <div className="pcg-name">{product.name}</div>

                {cheapestOwn && (
                  <div className="pcg-mixed-summary-row">
                    <span className="pcg-mixed-label">Own brand</span>
                    <span className="pcg-mixed-price">£{cheapestOwn.price.toFixed(2)}</span>
                  </div>
                )}
                {cheapestBranded && (
                  <div className="pcg-mixed-summary-row">
                    <span className="pcg-mixed-label">Branded</span>
                    <span className="pcg-mixed-price">£{cheapestBranded.price.toFixed(2)}</span>
                  </div>
                )}
                {pennyLine && (
                  <div className="pcg-penny-line">
                    <span className="pcg-penny-emoji">💡</span>
                    <span>{pennyLine}</span>
                  </div>
                )}
              </div>

              {/* ── Expanded: top stores only, no product names ── */}
              <div className="pcg-expanded">
                <div className="pcg-expanded-inner">

                  {expandedRows.slice(0, 3).map((p, i) => (
                    <div key={p.storeId} className={`pcg-compare-row${i === 0 ? ' cheapest' : ''}`}>
                      <span className="pcg-cstore">{getStoreName(p.storeId)}</span>
                      <span className="pcg-cprice">£{p.price.toFixed(2)}</span>
                    </div>
                  ))}
                  {expandedRows.length > 3 && (
                    <div className="pcg-more-row">+{expandedRows.length - 3} more {expandedRows.length - 3 === 1 ? 'store' : 'stores'}</div>
                  )}

                  <div className="pcg-action-row">
                    <button
                      className="pcg-add-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onAddToBasket) {
                          const cheapest = cheapestOwn || cheapestBranded;
                          if (cheapest) onAddToBasket(product, [cheapest]);
                        }
                      }}
                    >
                      Add
                    </button>
                    {onSelectProduct && (
                      <button
                        className="pcg-detail-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectProduct(product);
                        }}
                      >
                        Compare
                      </button>
                    )}
                  </div>

                </div>
              </div>
            </div>
          );
        }

        // ── Standard (non-mixed) card ─────────────────────────────────────
        const sortedPrices  = getSortedStorePrices(product);
        const cheapestStore = sortedPrices[0];
        const storeCount    = new Set(sortedPrices.map(p => p.storeId)).size;

        return (
          <div
            key={product.id}
            className={`pcg-card${isExpanded ? ' expanded' : ''}`}
            onClick={() => setExpandedCardId(isExpanded ? null : product.id)}
          >
            <div
              className="pcg-card-image"
              style={iconUrl ? undefined : { background: getCategoryGradient(product.category) }}
              aria-hidden="true"
            >
              {iconUrl
                ? <img src={iconUrl} alt="" className="pcg-card-photo" />
                : <span className="pcg-card-icon">{getCategoryIcon(product.category)}</span>
              }
            </div>

            {/* ── Collapsed ── */}
            <div className="pcg-collapsed">
              <div className="pcg-name">{product.name}</div>
              {cheapestStore && (
                <div className="pcg-price">from £{cheapestStore.price.toFixed(2)}</div>
              )}
              {cheapestStore && (
                <div className="pcg-store-row">
                  <span className="pcg-cheapest-line">
                    {storeCount > 1
                      ? `${cheapestStore.storeName} cheapest`
                      : cheapestStore.storeName}
                    {storeCount > 1 && (
                      <span className="pcg-more-inline"> · {storeCount} stores</span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* ── Expanded: top 3 stores + overflow count ── */}
            <div className="pcg-expanded">
              <div className="pcg-expanded-inner">
                {(() => {
                  const byStore = cheapestAbsDedupeByStore(sortedPrices);
                  return (
                    <>
                      {byStore.slice(0, 3).map((sp, i) => (
                        <div
                          key={sp.storeId}
                          className={`pcg-compare-row${i === 0 ? ' cheapest' : ''}`}
                        >
                          <span className="pcg-cstore">{sp.storeName}</span>
                          <span className="pcg-cprice">£{sp.price.toFixed(2)}</span>
                        </div>
                      ))}
                      {byStore.length > 3 && (
                        <div className="pcg-more-row">+{byStore.length - 3} more {byStore.length - 3 === 1 ? 'store' : 'stores'}</div>
                      )}
                    </>
                  );
                })()}

                <div className="pcg-action-row">
                  <button
                    className="pcg-add-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onAddToBasket) {
                        onAddToBasket(product, sortedPrices.map(sp => ({
                          storeId: sp.storeId,
                          storeName: sp.storeName,
                          price: sp.price
                        })));
                      }
                    }}
                  >
                    Add
                  </button>
                  {onSelectProduct && (
                    <button
                      className="pcg-detail-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectProduct(product);
                      }}
                    >
                      Compare
                    </button>
                  )}
                </div>

              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ProductCardGrid;
